import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import { engagementAPI } from '../services/api';
import { useAuth } from './AuthContext';

// Types
export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    tension?: number;
    pointBackgroundColor?: string;
    pointBorderColor?: string;
    pointRadius?: number;
    pointHoverRadius?: number;
  }[];
}

export interface AIInsight {
  type: 'insight' | 'prediction' | 'warning' | 'recommendation';
  title: string;
  message: string;
  icon: string;
  color: string;
}

export interface AIAnalysis {
  insights: AIInsight[];
}

export interface EngagementFeedbacks {
  positives: string[];
  negatives: string[];
  suggestions: string[];
  complaints: string[];
}

export interface EngagementState {
  sentiment: ChartData | null;
  responseTime: ChartData | null;
  problemSolving: ChartData | null;
  feedbackType: ChartData | null;
  trend: ChartData | null;
  feedbacks: EngagementFeedbacks | null;
  aiAnalysis: {
    [key: string]: AIAnalysis | null;
  };
  loading: {
    sentiment: boolean;
    responseTime: boolean;
    problemSolving: boolean;
    feedbackType: boolean;
    trend: boolean;
    feedbacks: boolean;
    aiAnalysis: {
      [key: string]: boolean;
    };
  };
  error: {
    sentiment: string | null;
    responseTime: string | null;
    problemSolving: string | null;
    feedbackType: string | null;
    trend: string | null;
    feedbacks: string | null;
    aiAnalysis: {
      [key: string]: string | null;
    };
  };
}

export type EngagementAction =
  | { type: 'FETCH_SENTIMENT_START' }
  | { type: 'FETCH_SENTIMENT_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_SENTIMENT_ERROR'; payload: string }
  | { type: 'FETCH_RESPONSE_TIME_START' }
  | { type: 'FETCH_RESPONSE_TIME_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_RESPONSE_TIME_ERROR'; payload: string }
  | { type: 'FETCH_PROBLEM_SOLVING_START' }
  | { type: 'FETCH_PROBLEM_SOLVING_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_PROBLEM_SOLVING_ERROR'; payload: string }
  | { type: 'FETCH_FEEDBACK_TYPE_START' }
  | { type: 'FETCH_FEEDBACK_TYPE_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_FEEDBACK_TYPE_ERROR'; payload: string }
  | { type: 'FETCH_TREND_START' }
  | { type: 'FETCH_TREND_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_TREND_ERROR'; payload: string }
  | { type: 'FETCH_FEEDBACKS_START' }
  | { type: 'FETCH_FEEDBACKS_SUCCESS'; payload: EngagementFeedbacks }
  | { type: 'FETCH_FEEDBACKS_ERROR'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_START'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_SUCCESS'; payload: { chartType: string; data: AIAnalysis } }
  | { type: 'FETCH_AI_ANALYSIS_ERROR'; payload: { chartType: string; error: string } };

const initialState: EngagementState = {
  sentiment: null,
  responseTime: null,
  problemSolving: null,
  feedbackType: null,
  trend: null,
  feedbacks: null,
  aiAnalysis: {},
  loading: {
    sentiment: false,
    responseTime: false,
    problemSolving: false,
    feedbackType: false,
    trend: false,
    feedbacks: false,
    aiAnalysis: {},
  },
  error: {
    sentiment: null,
    responseTime: null,
    problemSolving: null,
    feedbackType: null,
    trend: null,
    feedbacks: null,
    aiAnalysis: {},
  },
};

const engagementReducer = (state: EngagementState, action: EngagementAction): EngagementState => {
  switch (action.type) {
    case 'FETCH_SENTIMENT_START':
      return {
        ...state,
        loading: { ...state.loading, sentiment: true },
        error: { ...state.error, sentiment: null },
      };
    case 'FETCH_SENTIMENT_SUCCESS':
      return {
        ...state,
        sentiment: action.payload,
        loading: { ...state.loading, sentiment: false },
      };
    case 'FETCH_SENTIMENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, sentiment: false },
        error: { ...state.error, sentiment: action.payload },
      };
    case 'FETCH_RESPONSE_TIME_START':
      return {
        ...state,
        loading: { ...state.loading, responseTime: true },
        error: { ...state.error, responseTime: null },
      };
    case 'FETCH_RESPONSE_TIME_SUCCESS':
      return {
        ...state,
        responseTime: action.payload,
        loading: { ...state.loading, responseTime: false },
      };
    case 'FETCH_RESPONSE_TIME_ERROR':
      return {
        ...state,
        loading: { ...state.loading, responseTime: false },
        error: { ...state.error, responseTime: action.payload },
      };
    case 'FETCH_PROBLEM_SOLVING_START':
      return {
        ...state,
        loading: { ...state.loading, problemSolving: true },
        error: { ...state.error, problemSolving: null },
      };
    case 'FETCH_PROBLEM_SOLVING_SUCCESS':
      return {
        ...state,
        problemSolving: action.payload,
        loading: { ...state.loading, problemSolving: false },
      };
    case 'FETCH_PROBLEM_SOLVING_ERROR':
      return {
        ...state,
        loading: { ...state.loading, problemSolving: false },
        error: { ...state.error, problemSolving: action.payload },
      };
    case 'FETCH_FEEDBACK_TYPE_START':
      return {
        ...state,
        loading: { ...state.loading, feedbackType: true },
        error: { ...state.error, feedbackType: null },
      };
    case 'FETCH_FEEDBACK_TYPE_SUCCESS':
      return {
        ...state,
        feedbackType: action.payload,
        loading: { ...state.loading, feedbackType: false },
      };
    case 'FETCH_FEEDBACK_TYPE_ERROR':
      return {
        ...state,
        loading: { ...state.loading, feedbackType: false },
        error: { ...state.error, feedbackType: action.payload },
      };
    case 'FETCH_TREND_START':
      return {
        ...state,
        loading: { ...state.loading, trend: true },
        error: { ...state.error, trend: null },
      };
    case 'FETCH_TREND_SUCCESS':
      return {
        ...state,
        trend: action.payload,
        loading: { ...state.loading, trend: false },
      };
    case 'FETCH_TREND_ERROR':
      return {
        ...state,
        loading: { ...state.loading, trend: false },
        error: { ...state.error, trend: action.payload },
      };
    case 'FETCH_FEEDBACKS_START':
      return {
        ...state,
        loading: { ...state.loading, feedbacks: true },
        error: { ...state.error, feedbacks: null },
      };
    case 'FETCH_FEEDBACKS_SUCCESS':
      return {
        ...state,
        feedbacks: action.payload,
        loading: { ...state.loading, feedbacks: false },
      };
    case 'FETCH_FEEDBACKS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, feedbacks: false },
        error: { ...state.error, feedbacks: action.payload },
      };
    case 'FETCH_AI_ANALYSIS_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload]: true,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload]: null,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_SUCCESS':
      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          [action.payload.chartType]: action.payload.data,
        },
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload.chartType]: null,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload.chartType]: action.payload.error,
          },
        },
      };
    default:
      return state;
  }
};

interface EngagementContextType {
  state: EngagementState;
  fetchSentimentData: () => Promise<void>;
  fetchResponseTimeData: () => Promise<void>;
  fetchProblemSolvingData: () => Promise<void>;
  fetchFeedbackTypeData: () => Promise<void>;
  fetchTrendData: () => Promise<void>;
  fetchFeedbacksData: () => Promise<void>;
  fetchAIAnalysis: (chartType: string) => Promise<void>;
  fetchAllEngagementData: () => Promise<void>;
}

const EngagementContext = createContext<EngagementContextType | undefined>(undefined);

export const EngagementProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(engagementReducer, initialState);
  const { business } = useAuth();

  const fetchSentimentData = useCallback(async () => {
    if (!business?._id) return;
    dispatch({ type: 'FETCH_SENTIMENT_START' });
    try {
      const response = await engagementAPI.getSentimentData(business._id);
      dispatch({ type: 'FETCH_SENTIMENT_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_SENTIMENT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchResponseTimeData = useCallback(async () => {
    if (!business?._id) return;
    dispatch({ type: 'FETCH_RESPONSE_TIME_START' });
    try {
      const response = await engagementAPI.getResponseTimeData(business._id);
      dispatch({ type: 'FETCH_RESPONSE_TIME_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_RESPONSE_TIME_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchProblemSolvingData = useCallback(async () => {
    if (!business?._id) return;
    dispatch({ type: 'FETCH_PROBLEM_SOLVING_START' });
    try {
      const response = await engagementAPI.getProblemSolvingData(business._id);
      dispatch({ type: 'FETCH_PROBLEM_SOLVING_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_PROBLEM_SOLVING_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchFeedbackTypeData = useCallback(async () => {
    if (!business?._id) return;
    dispatch({ type: 'FETCH_FEEDBACK_TYPE_START' });
    try {
      const response = await engagementAPI.getFeedbackTypeData(business._id);
      dispatch({ type: 'FETCH_FEEDBACK_TYPE_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_FEEDBACK_TYPE_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchTrendData = useCallback(async () => {
    if (!business?._id) return;
    dispatch({ type: 'FETCH_TREND_START' });
    try {
      const response = await engagementAPI.getTrendData(business._id);
      dispatch({ type: 'FETCH_TREND_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_TREND_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchAIAnalysis = useCallback(async (chartType: string) => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: chartType });
    try {
      const response = await engagementAPI.getAIAnalysis(business._id, chartType);
      dispatch({
        type: 'FETCH_AI_ANALYSIS_SUCCESS',
        payload: { chartType, data: response.data.data },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: 'FETCH_AI_ANALYSIS_ERROR',
          payload: { chartType, error: 'PREPARING' },
        });
      } else {
        dispatch({
          type: 'FETCH_AI_ANALYSIS_ERROR',
          payload: { chartType, error: 'AI analizi yüklenirken hata oluştu' },
        });
      }
    }
  }, [business?._id]);

  const fetchFeedbacksData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_FEEDBACKS_START' });
    try {
      const response = await engagementAPI.getFeedbacksData(business._id);
      dispatch({ type: 'FETCH_FEEDBACKS_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_FEEDBACKS_ERROR', payload: 'PREPARING' });
      } else {
        dispatch({ type: 'FETCH_FEEDBACKS_ERROR', payload: 'Feedbacks verileri yüklenirken hata oluştu' });
      }
    }
  }, [business?._id]);

  // Tüm engagement verisini tek seferde çekip state'e dağıtan fonksiyon
  const fetchAllEngagementData = useCallback(async () => {
    if (!business?._id) return;
    
    try {
      // Tüm loading state'lerini başlat
      dispatch({ type: 'FETCH_SENTIMENT_START' });
      dispatch({ type: 'FETCH_RESPONSE_TIME_START' });
      dispatch({ type: 'FETCH_PROBLEM_SOLVING_START' });
      dispatch({ type: 'FETCH_FEEDBACK_TYPE_START' });
      dispatch({ type: 'FETCH_TREND_START' });
      dispatch({ type: 'FETCH_FEEDBACKS_START' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'sentiment' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'responseTime' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'problemSolving' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'feedbackType' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'feedbacks' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'trend' });

      const response = await engagementAPI.getAllEngagementData(business._id, 'engagement');
      // API'den gelen data bir array, ilk elemanı alıyoruz
      const dataArray = response.data.data;
      const engagementData = Array.isArray(dataArray) && dataArray.length > 0 ? dataArray[0] : dataArray;
      
      // Debug: API response'unu kontrol et
      console.log('Engagement API Response:', {
        dataArray,
        engagementData,
        aiAnalysis: engagementData?.data?.aiAnalysis
      });

      // Sentiment data - API'de sentiment verisi yok, PREPARING olarak işaretle
      dispatch({ type: 'FETCH_SENTIMENT_ERROR', payload: 'PREPARING' });

      // Response Time data
      if (engagementData?.data?.responseTime) {
        dispatch({ type: 'FETCH_RESPONSE_TIME_SUCCESS', payload: engagementData.data.responseTime });
      } else {
        dispatch({ type: 'FETCH_RESPONSE_TIME_ERROR', payload: 'PREPARING' });
      }

      // Problem Solving data
      if (engagementData?.data?.problemSolving) {
        dispatch({ type: 'FETCH_PROBLEM_SOLVING_SUCCESS', payload: engagementData.data.problemSolving });
      } else {
        dispatch({ type: 'FETCH_PROBLEM_SOLVING_ERROR', payload: 'PREPARING' });
      }

      // Feedback Type data
      if (engagementData?.data?.feedbackType) {
        dispatch({ type: 'FETCH_FEEDBACK_TYPE_SUCCESS', payload: engagementData.data.feedbackType });
      } else {
        dispatch({ type: 'FETCH_FEEDBACK_TYPE_ERROR', payload: 'PREPARING' });
      }

      // Trend data
      if (engagementData?.data?.trend) {
        dispatch({ type: 'FETCH_TREND_SUCCESS', payload: engagementData.data.trend });
      } else {
        dispatch({ type: 'FETCH_TREND_ERROR', payload: 'PREPARING' });
      }

      // Feedbacks data
      if (engagementData?.data?.feedbacks) {
        dispatch({ type: 'FETCH_FEEDBACKS_SUCCESS', payload: engagementData.data.feedbacks });
      } else {
        dispatch({ type: 'FETCH_FEEDBACKS_ERROR', payload: 'PREPARING' });
      }

      // AI Analysis
      // API'den gelen key'ler camelCase formatında: responseTime, problemSolving, feedbackType, feedbacks, trend
      // State'te kullanılan key'ler de aynı: responseTime, problemSolving, feedbackType, feedbacks, trend
      const analysisTypes = ['responseTime', 'problemSolving', 'feedbackType', 'feedbacks', 'trend'];
      
      analysisTypes.forEach((chartType) => {
        const aiAnalysisData = engagementData?.data?.aiAnalysis?.[chartType];
        console.log(`AI Analysis [${chartType}]:`, {
          exists: !!aiAnalysisData,
          data: aiAnalysisData
        });
        
        if (aiAnalysisData) {
          dispatch({ 
            type: 'FETCH_AI_ANALYSIS_SUCCESS', 
            payload: { chartType, data: aiAnalysisData } 
          });
        } else {
          dispatch({ 
            type: 'FETCH_AI_ANALYSIS_ERROR', 
            payload: { chartType, error: 'PREPARING' } 
          });
        }
      });
      
      // Sentiment için AI analysis yok, PREPARING olarak işaretle
      dispatch({ 
        type: 'FETCH_AI_ANALYSIS_ERROR', 
        payload: { chartType: 'sentiment', error: 'PREPARING' } 
      });

    } catch (error: any) {
      // Hata durumunda tüm state'leri PREPARING olarak işaretle
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_SENTIMENT_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_RESPONSE_TIME_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_PROBLEM_SOLVING_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_FEEDBACK_TYPE_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_TREND_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_FEEDBACKS_ERROR', payload: 'PREPARING' });
        ['sentiment', 'responseTime', 'problemSolving', 'feedbackType', 'feedbacks', 'trend'].forEach(chartType => {
          dispatch({ 
            type: 'FETCH_AI_ANALYSIS_ERROR', 
            payload: { chartType, error: 'PREPARING' } 
          });
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Bilinmeyen hata';
        dispatch({ type: 'FETCH_SENTIMENT_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_RESPONSE_TIME_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_PROBLEM_SOLVING_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_FEEDBACK_TYPE_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_TREND_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_FEEDBACKS_ERROR', payload: errorMessage });
        ['sentiment', 'responseTime', 'problemSolving', 'feedbackType', 'feedbacks', 'trend'].forEach(chartType => {
          dispatch({ 
            type: 'FETCH_AI_ANALYSIS_ERROR', 
            payload: { chartType, error: errorMessage } 
          });
        });
      }
    }
  }, [business?._id]);

  return (
    <EngagementContext.Provider
      value={{
        state,
        fetchSentimentData,
        fetchResponseTimeData,
        fetchProblemSolvingData,
        fetchFeedbackTypeData,
        fetchTrendData,
        fetchFeedbacksData,
        fetchAIAnalysis,
        fetchAllEngagementData,
      }}
    >
      {children}
    </EngagementContext.Provider>
  );
};

export const useEngagement = (): EngagementContextType => {
  const context = useContext(EngagementContext);
  if (context === undefined) {
    throw new Error('useEngagement must be used within an EngagementProvider');
  }
  return context;
};
